import { keepPreviousData, queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { DateRangeParams } from '@/api/apis/resources/schedule';
import { scheduleQueryKeys } from '@/queries/resource/schedule/schedule.keys';

export const omsScheduleQueries = {
  shifts: (dateRange: DateRangeParams, processId?: string | null) => {
    const queryFn = () =>
      processId
        ? api.schedule.oms.getOMSPlanningShifts(processId, dateRange)
        : api.schedule.oms.getOMSShifts(dateRange);

    return queryOptions({
      queryKey: scheduleQueryKeys.oms.getShiftsKey(dateRange, processId),
      queryFn: () => queryFn().then((data) => data.resourcesShifts),
    });
  },
  schedule: (startDayOfMonth: Date) =>
    queryOptions({
      queryKey: scheduleQueryKeys.oms.getScheduleKey(startDayOfMonth),
      queryFn: () => api.schedule.oms.getOMSSchedule(startDayOfMonth),
    }),
  planningProcess: (processId?: string | null) =>
    queryOptions({
      queryKey: scheduleQueryKeys.oms.getPlanningProcessKey(processId),
      queryFn: processId ? () => api.schedule.oms.getOMSPlanningProcessById(processId) : skipToken,
    }),
  validation: (scheduleId?: string) =>
    queryOptions({
      queryKey: scheduleQueryKeys.oms.getValidationKey(scheduleId),
      queryFn: scheduleId ? () => api.schedule.oms.getOMSValidation(scheduleId) : skipToken,
    }),
  hrSystemConflicts: (scheduleId?: string) =>
    queryOptions({
      queryKey: scheduleQueryKeys.oms.getHrSystemConflictsKey(scheduleId),
      queryFn: scheduleId ? () => api.schedule.oms.getOMSScheduleHrSystemConflicts(scheduleId) : skipToken,
    }),
  rules: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.oms.getRulesKey(),
      queryFn: () => api.schedule.oms.getOMSScheduleRules(),
    }),
  inputs: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.oms.getInputsKey(),
      queryFn: () => api.schedule.oms.getOMSScheduleInputs(),
    }),
};

export const useOMSScheduleShiftsQuery = (dateRange: DateRangeParams, processId?: string | null, isPlanned = false) => {
  return useQuery({
    ...omsScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined),
    placeholderData: keepPreviousData,
  });
};

export const useOMSMonthScheduleQuery = (startDayOfMonth: Date, refetchTime?: number) => {
  return useQuery({
    ...omsScheduleQueries.schedule(startDayOfMonth),
    refetchInterval: refetchTime,
  });
};

export const useOMSPlanningProcessQuery = (processId?: string | null, enabled = false, refetchTime?: number) => {
  return useQuery({
    ...omsScheduleQueries.planningProcess(processId),
    enabled: Boolean(processId && enabled),
    refetchInterval: refetchTime,
  });
};

export const useOMSValidationQuery = (scheduleId?: string, enabled = false) => {
  return useQuery({
    ...omsScheduleQueries.validation(scheduleId),
    enabled: Boolean(scheduleId && enabled),
  });
};

export const useOMSHrSystemConflictsQuery = (scheduleId?: string, enabled = false) => {
  return useQuery({
    ...omsScheduleQueries.hrSystemConflicts(scheduleId),
    enabled: Boolean(scheduleId && enabled),
  });
};

export const useOMSScheduleSettingsRulesQuery = () => {
  return useQuery(omsScheduleQueries.rules());
};

export const useOMSScheduleSettingsInputsQuery = () => {
  return useQuery(omsScheduleQueries.inputs());
};
